import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Shield, Star, Briefcase, DollarSign, Users, Heart, BookOpen, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { 
  fadeInUp, 
  fadeInLeft, 
  platformCard,
  viewportSettings
} from '../hooks/useScrollAnimation';

export const Platform = () => {
  const platformItems = [
    { icon: Shield, title: 'Safety and Security', description: 'Protecting the public starts inside the jail. I\'ll ensure the highest standards of safety for staff, inmates, and visitors through strong leadership, consistent training, and clear accountability.' },
    { icon: Star, title: 'Professional Leadership', description: 'A good jail runs on respect and teamwork. I\'ll lead by example, promote professionalism at every level, and support our officers so they can perform their duties with pride and confidence.' },
    { icon: Briefcase, title: 'Rehabilitation Through Work', description: 'I believe in second chances built on hard work and accountability. By expanding inmate work programs, vocational training, and life-skills education, we can help individuals leave jail ready to contribute to society.' },
    { icon: DollarSign, title: 'Fiscal Responsibility', description: 'Use taxpayer dollars wisely through efficient operations and smart management of contracts, including food service, medical care, and maintenance, to ensure quality while reducing unnecessary costs.' },
    { icon: Users, title: 'Community Partnership', description: 'We\'re stronger when we work together. I\'ll collaborate with local law enforcement, the courts, and community organizations to reduce repeat offenses and create a safer Marshall County.' },
    { icon: Heart, title: 'Mental Health Services', description: 'Programs and support that help individuals manage emotional, behavioral, or psychological challenges. These services include counseling, therapy, addiction recovery, and crisis intervention to help inmates cope, heal, and prepare for a better life after release.' },
    { icon: BookOpen, title: 'Ministry Programs', description: 'Acts of service or outreach rooted in faith, offering spiritual guidance, encouragement, and hope to those in need. Ministry programs help individuals find purpose, forgiveness, and a new direction in life through faith-based support and mentoring.' }
  ];

  const commitments = [
    'Lead with integrity and hold myself accountable to the highest standards.',
    'Ensure the safety of staff, inmates, and the public at all times.',
    'Provide opportunities for rehabilitation that reduce repeat offenses.',
    'Manage resources responsibly and transparently.',
    'Build strong partnerships with law enforcement and community organizations.'
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16 sm:pt-20">
      <SEO 
        title="Platform & Priorities | Barry Howell for MC Jailer"
        description="Explore Barry Howell's platform for the Marshall County Jail: Safety, Professional Leadership, and Rehabilitation. Improving Marshall County, KY's public safety."
        keywords="Marshall County Jailer platform, safety, rehabilitation, fiscal responsibility, Barry Howell issues, Marshall County Jail policies"
        canonical="https://mcjailer.com/platform"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Platform & Priorities",
            "description": "Barry Howell's comprehensive plan for the Marshall County Jail.",
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Safety and Security" },
                { "@type": "ListItem", "position": 2, "name": "Professional Leadership" },
                { "@type": "ListItem", "position": 3, "name": "Rehabilitation Through Work" },
                { "@type": "ListItem", "position": 4, "name": "Fiscal Responsibility" },
                { "@type": "ListItem", "position": 5, "name": "Community Partnership" },
                { "@type": "ListItem", "position": 6, "name": "Mental Health Services" },
                { "@type": "ListItem", "position": 7, "name": "Ministry Programs" }
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_jailer-campaign/artifacts/88ptp2yb_DSC09792.jpg"
            alt="Background"
            className="w-full h-full object-cover object-top brightness-[0.3] contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/40 to-[#0A0A0A]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
              My Vision
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white uppercase tracking-tighter font-['Barlow_Condensed']">
              The Platform
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 mt-4 sm:mt-6 max-w-3xl mx-auto px-4">
              A comprehensive plan for safer facilities, professional operations, and meaningful rehabilitation in Marshall County.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why I'm Running */}
      <section className="py-10 sm:py-16 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="max-w-4xl mx-auto text-center" 
            data-testid="why-running"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <Shield className="w-12 h-12 sm:w-16 sm:h-16 text-[#DAA520] mx-auto mb-6 sm:mb-8" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 sm:mb-6">
              Why I'm Running
            </h2>
            <div className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed space-y-4">
              <p>After 15 years of dedicated service with the Marshall County Sheriff's Office, I've seen firsthand what works and what needs improvement in our local justice system. I've experienced the most rewarding moments and some of the toughest challenges that come with public service.</p>
              <p>But through it all, one thing has remained constant — <span className="text-[#DAA520] font-semibold">my love for this county</span>.</p>
              <p>I'm running for County Jailer because I believe in bringing experienced, professional leadership to this critical role. Our community deserves a jail that prioritizes safety, promotes rehabilitation, and operates efficiently with taxpayer dollars.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platform Grid */}
      <section className="py-12 sm:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="text-center mb-10 sm:mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <p className="text-[#DAA520] text-sm uppercase tracking-[0.3em] mb-3 sm:mb-4">
              Core Priorities
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed']">
              What I Stand For
            </h2>
            <div className="h-1 w-20 bg-[#DAA520] mx-auto mt-4 sm:mt-6"></div>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8" data-testid="platform-grid">
            {platformItems.map((item, index) => (
              <motion.div 
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={viewportSettings}
                variants={platformCard(index)}
                className="bg-[#111111] border border-[#DAA520]/20 hover:border-[#DAA520]/50 transition-colors duration-300 group flex flex-col"
              >
                <div className="p-5 sm:p-6 md:p-8 flex-grow">
                  <item.icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#DAA520] mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-lg sm:text-xl font-bold text-white uppercase tracking-wide font-['Barlow_Condensed'] mb-3 sm:mb-4">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
                    {item.description}
                  </p>
                </div>
                <div className="h-1 bg-[#DAA520] w-0 group-hover:w-full transition-all duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Statement */}
      <section className="py-12 sm:py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInLeft}
          >
            <div className="bg-[#0A0A0A] border-l-4 border-[#DAA520] p-6 sm:p-8 md:p-12">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 sm:mb-6">
                My Commitment to You
              </h3>
              <ul className="space-y-3 sm:space-y-4 text-gray-300 text-sm sm:text-base">
                {commitments.map((item, index) => (
                  <li key={index} className="flex items-start gap-3 sm:gap-4">
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-[#DAA520] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-[#0A0A0A] to-[#1F2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportSettings}
            variants={fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white uppercase tracking-tight font-['Barlow_Condensed'] mb-4 sm:mb-6">
              Support This Vision
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto mb-6 sm:mb-8">
              Together, we can make Marshall County safer and stronger. Join the campaign today.
            </p>
            <Link to="/stay-informed">
              <Button 
                data-testid="platform-cta-btn"
                className="bg-[#DAA520] text-black hover:bg-[#E6B422] font-bold uppercase tracking-wider px-8 sm:px-10 py-4 sm:py-6 text-base sm:text-lg gold-glow"
              >
                Get Involved
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
